import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '../core/environment';

interface Lookup {
  id: string;
  name: string;
}

interface BatchLookup {
  id: string;
  name: string;
  sportId: string;
  sportName: string;
}

interface StudentItem {
  id: string;
  membershipCode: string;
  fullName: string;
  mobile: string;
}

interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

interface SummerCampProgram {
  id: string;
  name: string;
  sportId: string;
  sportName: string;
  packageDays: number;
  startDate: string;
  endDate: string;
  fee: number;
  capacity: number;
  registeredCount: number;
  isActive: boolean;
}

interface SummerCampRegistration {
  id: string;
  programName: string;
  studentName: string;
  trainerName?: string;
  batchName?: string;
  registrationDate: string;
  feeAmount: number;
  paymentStatus: number;
  completionStatus: string;
}

@Component({
  selector: 'app-summer-camp',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="summer-page">
      <div class="summer-hero">
        <div>
          <span>Summer Camp Operations</span>
          <h2>Summer Camp Registration</h2>
          <p>Create 7, 15, and 30 day camp programs, assign sports and trainers, then register students with fee tracking.</p>
        </div>
        <button type="button" (click)="load()">Refresh</button>
      </div>

      <section class="summer-stats">
        <article>
          <span>Active Programs</span>
          <strong>{{ programs().length }}</strong>
          <p>Across all sports</p>
        </article>
        <article>
          <span>Total Registrations</span>
          <strong>{{ registrations().length }}</strong>
          <p>Current season</p>
        </article>
        <article>
          <span>Expected Revenue</span>
          <strong>Rs {{ expectedRevenue() }}</strong>
          <p>From camp fees</p>
        </article>
        <article>
          <span>Open Seats</span>
          <strong>{{ openSeats() }}</strong>
          <p>Available capacity</p>
        </article>
      </section>

      <section class="summer-grid">
        <form class="summer-card summer-form" [formGroup]="programForm" (ngSubmit)="createProgram()">
          <div class="card-title">
            <div>
              <h3>Create Camp Program</h3>
              <p>Setup package, dates, sport, capacity and fee.</p>
            </div>
            <span>Setup</span>
          </div>

          <label>Program Name <input formControlName="name" placeholder="Swimming Summer Camp - Batch A"></label>
          <label>Sport
            <select formControlName="sportId">
              @for (sport of sports(); track sport.id) {
                <option [value]="sport.id">{{ sport.name }}</option>
              }
            </select>
          </label>
          <div class="two-col">
            <label>Package
              <select formControlName="packageDays">
                <option [value]="7">7 Days</option>
                <option [value]="15">15 Days</option>
                <option [value]="30">30 Days</option>
              </select>
            </label>
            <label>Capacity <input type="number" formControlName="capacity"></label>
          </div>
          <div class="two-col">
            <label>Start Date <input type="date" formControlName="startDate"></label>
            <label>End Date <input type="date" formControlName="endDate"></label>
          </div>
          <label>Fee <input type="number" formControlName="fee"></label>

          @if (programMessage()) {
            <p class="summer-message">{{ programMessage() }}</p>
          }

          <button class="summer-primary" type="submit" [disabled]="programForm.invalid">Create Program</button>
        </form>

        <form class="summer-card summer-form" [formGroup]="registrationForm" (ngSubmit)="registerStudent()">
          <div class="card-title">
            <div>
              <h3>Register Student</h3>
              <p>Assign student, trainer, batch and payment state.</p>
            </div>
            <span>Admission</span>
          </div>

          <label>Camp Program
            <select formControlName="programId">
              @for (program of programs(); track program.id) {
                <option [value]="program.id">{{ program.name }} - {{ program.packageDays }} days</option>
              }
            </select>
          </label>
          <label>Student
            <select formControlName="studentId">
              @for (student of students(); track student.id) {
                <option [value]="student.id">{{ student.fullName }} - {{ student.mobile }}</option>
              }
            </select>
          </label>
          <label>Trainer
            <select formControlName="trainerStaffId">
              <option [ngValue]="null">Not assigned</option>
              @for (staff of staffMembers(); track staff.id) {
                <option [value]="staff.id">{{ staff.name }}</option>
              }
            </select>
          </label>
          <label>Batch
            <select formControlName="batchId">
              <option [ngValue]="null">Not assigned</option>
              @for (batch of batches(); track batch.id) {
                <option [value]="batch.id">{{ batch.name }}</option>
              }
            </select>
          </label>
          <label class="check-row"><input type="checkbox" formControlName="markPaid"> Mark payment as paid</label>

          @if (registrationMessage()) {
            <p class="summer-message">{{ registrationMessage() }}</p>
          }

          <button class="summer-primary" type="submit" [disabled]="registrationForm.invalid">Register Student</button>
        </form>
      </section>

      <section class="summer-grid summer-grid-wide">
        <article class="summer-card">
          <div class="card-title">
            <div>
              <h3>Camp Programs</h3>
              <p>Package-wise capacity and fee overview.</p>
            </div>
          </div>
          <div class="program-list">
            @for (program of programs(); track program.id) {
              <div class="program-row">
                <div>
                  <strong>{{ program.name }}</strong>
                  <span>{{ program.sportName }} | {{ program.packageDays }} days | {{ program.startDate }} to {{ program.endDate }}</span>
                </div>
                <div class="program-numbers">
                  <strong>Rs {{ program.fee }}</strong>
                  <span>{{ program.registeredCount }}/{{ program.capacity }} seats</span>
                </div>
              </div>
            }
          </div>
        </article>

        <article class="summer-card">
          <div class="card-title">
            <div>
              <h3>Recent Registrations</h3>
              <p>Student registrations, payment state and completion status.</p>
            </div>
          </div>
          <table>
            <thead>
              <tr><th>Student</th><th>Program</th><th>Fee</th><th>Payment</th><th>Status</th></tr>
            </thead>
            <tbody>
              @for (registration of registrations(); track registration.id) {
                <tr>
                  <td>{{ registration.studentName }}</td>
                  <td>{{ registration.programName }}</td>
                  <td>Rs {{ registration.feeAmount }}</td>
                  <td><span class="status-chip">{{ registration.paymentStatus === 3 ? 'Paid' : 'Pending' }}</span></td>
                  <td>{{ registration.completionStatus }}</td>
                </tr>
              }
            </tbody>
          </table>
        </article>
      </section>
    </div>
  `
})
export class SummerCampComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);

  readonly sports = signal<Lookup[]>([]);
  readonly students = signal<StudentItem[]>([]);
  readonly staffMembers = signal<Lookup[]>([]);
  readonly batches = signal<BatchLookup[]>([]);
  readonly programs = signal<SummerCampProgram[]>([]);
  readonly registrations = signal<SummerCampRegistration[]>([]);
  readonly programMessage = signal('');
  readonly registrationMessage = signal('');

  readonly programForm = this.fb.nonNullable.group({
    name: ['Summer Swimming Camp', Validators.required],
    sportId: ['', Validators.required],
    packageDays: [15, Validators.required],
    startDate: [new Date().toISOString().slice(0, 10), Validators.required],
    endDate: [this.addDays(15), Validators.required],
    fee: [4500, Validators.required],
    capacity: [30, Validators.required]
  });

  readonly registrationForm = this.fb.group({
    programId: ['', Validators.required],
    studentId: ['', Validators.required],
    trainerStaffId: [null as string | null],
    batchId: [null as string | null],
    markPaid: [false]
  });

  ngOnInit(): void {
    this.loadLookups();
    this.load();
  }

  expectedRevenue(): number {
    return this.registrations().reduce((total, item) => total + item.feeAmount, 0);
  }

  openSeats(): number {
    return this.programs().reduce((total, item) => total + Math.max(item.capacity - item.registeredCount, 0), 0);
  }

  createProgram(): void {
    if (this.programForm.invalid) return;
    this.programMessage.set('');
    this.http.post(`${environment.apiUrl}/summer-camps/programs`, this.programForm.getRawValue()).subscribe({
      next: () => {
        this.programMessage.set('Program created successfully.');
        this.load();
      },
      error: (error) => this.programMessage.set(error.error?.message ?? 'Unable to create program.')
    });
  }

  registerStudent(): void {
    if (this.registrationForm.invalid) return;
    this.registrationMessage.set('');
    this.http.post(`${environment.apiUrl}/summer-camps/registrations`, this.registrationForm.getRawValue()).subscribe({
      next: () => {
        this.registrationMessage.set('Student registered successfully.');
        this.load();
      },
      error: (error) => this.registrationMessage.set(error.error?.message ?? 'Unable to register student.')
    });
  }

  load(): void {
    this.http.get<SummerCampProgram[]>(`${environment.apiUrl}/summer-camps/programs`).subscribe((items) => {
      this.programs.set(items);
      if (items.length && !this.registrationForm.value.programId) {
        this.registrationForm.patchValue({ programId: items[0].id });
      }
    });

    this.http.get<SummerCampRegistration[]>(`${environment.apiUrl}/summer-camps/registrations`).subscribe((items) => this.registrations.set(items));
  }

  private loadLookups(): void {
    this.http.get<Lookup[]>(`${environment.apiUrl}/masters/sports`).subscribe((sports) => {
      this.sports.set(sports);
      const swimming = sports.find((sport) => sport.name === 'Swimming') ?? sports[0];
      this.programForm.patchValue({ sportId: swimming?.id ?? '' });
    });

    this.http.get<PagedResult<StudentItem>>(`${environment.apiUrl}/students?pageSize=100`).subscribe((result) => {
      this.students.set(result.items);
      if (result.items.length) {
        this.registrationForm.patchValue({ studentId: result.items[0].id });
      }
    });

    this.http.get<Lookup[]>(`${environment.apiUrl}/staff`).subscribe((items) => this.staffMembers.set(items));
    this.http.get<BatchLookup[]>(`${environment.apiUrl}/masters/batches`).subscribe((items) => this.batches.set(items));
  }

  private addDays(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  }
}
